import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { MaintenanceWindow, MaintenanceWindowFanout, Project, ProjectComponent, StatusPageSettings } from '../models/project.model';
import { ProjectAnalytics } from '../models/analytics.model';
import { CreatedProjectApiKey, ProjectApiKey } from '../models/api-keys.model';
import { ProjectNotificationSettings, TelegramLink, TestAlertResult } from '../models/notifications.model';
import { UrlConstantsService } from './url-constants.service';

@Injectable()
export class ProjectManagementService {
  constructor(
    private _http: HttpClient,
    private urls: UrlConstantsService
  ) {}

  public getProjects(): Observable<Project[]> {
    return this._http.get<Project[]>(this.urls.PROJECTS);
  }

  public getProject(id: string): Observable<Project> {
    return this._http.get<Project>(this.urls.PROJECT_BY_ID(id));
  }

  public createProject(data: Partial<Project>): Observable<Project> {
    return this._http.post<Project>(this.urls.PROJECTS, data);
  }

  public updateProject(id: string, data: Partial<Project>): Observable<Project> {
    return this._http.put<Project>(this.urls.PROJECT_BY_ID(id), data);
  }

  public deleteProject(id: string): Observable<void> {
    return this._http.delete<void>(this.urls.PROJECT_BY_ID(id));
  }

  public getComponents(projectId: string): Observable<ProjectComponent[]> {
    return this._http.get<ProjectComponent[]>(this.urls.PROJECT_COMPONENTS(projectId));
  }

  public addComponent(projectId: string, data: Partial<ProjectComponent>): Observable<ProjectComponent> {
    return this._http.post<ProjectComponent>(this.urls.PROJECT_COMPONENTS(projectId), data);
  }

  public updateComponent(projectId: string, componentId: number, data: Partial<ProjectComponent>): Observable<ProjectComponent> {
    return this._http.put<ProjectComponent>(this.urls.PROJECT_COMPONENT_BY_ID(projectId, componentId), data);
  }

  public deleteComponent(projectId: string, componentId: number): Observable<void> {
    return this._http.delete<void>(this.urls.PROJECT_COMPONENT_BY_ID(projectId, componentId));
  }

  public getStatusPage(projectId: string): Observable<StatusPageSettings> {
    return this._http.get<StatusPageSettings>(this.urls.PROJECT_STATUS_PAGE(projectId));
  }

  public updateStatusPage(projectId: string, data: Partial<StatusPageSettings>): Observable<StatusPageSettings> {
    return this._http.put<StatusPageSettings>(this.urls.PROJECT_STATUS_PAGE(projectId), data);
  }

  public getAnalytics(projectId: string): Observable<ProjectAnalytics> {
    return this._http.get<ProjectAnalytics>(this.urls.PROJECT_ANALYTICS(projectId));
  }

  public getMaintenanceWindows(projectId: string, componentId: number): Observable<MaintenanceWindow[]> {
    return this._http.get<MaintenanceWindow[]>(this.urls.COMPONENT_MAINTENANCE_WINDOWS(projectId, componentId));
  }

  public createMaintenanceWindow(projectId: string, componentId: number, data: Partial<MaintenanceWindow>): Observable<MaintenanceWindow> {
    return this._http.post<MaintenanceWindow>(this.urls.COMPONENT_MAINTENANCE_WINDOWS(projectId, componentId), data);
  }

  /** Apply one window to several components at once; returns the window created per component. */
  public createMaintenanceWindowForComponents(projectId: string, data: MaintenanceWindowFanout): Observable<MaintenanceWindow[]> {
    return this._http.post<MaintenanceWindow[]>(this.urls.PROJECT_MAINTENANCE_WINDOWS(projectId), data);
  }

  public deleteMaintenanceWindow(projectId: string, componentId: number, windowId: number): Observable<void> {
    return this._http.delete<void>(this.urls.COMPONENT_MAINTENANCE_WINDOW_BY_ID(projectId, componentId, windowId));
  }

  public getNotificationSettings(projectId: string): Observable<ProjectNotificationSettings> {
    return this._http.get<ProjectNotificationSettings>(this.urls.PROJECT_NOTIFICATIONS(projectId));
  }

  public updateNotificationSettings(projectId: string, data: Partial<ProjectNotificationSettings>): Observable<ProjectNotificationSettings> {
    return this._http.put<ProjectNotificationSettings>(this.urls.PROJECT_NOTIFICATIONS(projectId), data);
  }

  public sendTestAlert(projectId: string): Observable<TestAlertResult> {
    return this._http.post<TestAlertResult>(this.urls.PROJECT_NOTIFICATIONS_TEST(projectId), {});
  }

  public createTelegramLink(projectId: string): Observable<TelegramLink> {
    return this._http.post<TelegramLink>(this.urls.PROJECT_TELEGRAM_LINK(projectId), {});
  }

  public deleteTelegramChat(projectId: string, chatId: number): Observable<void> {
    return this._http.delete<void>(this.urls.PROJECT_TELEGRAM_CHAT_BY_ID(projectId, chatId));
  }

  public getApiKeys(projectId: string): Observable<ProjectApiKey[]> {
    return this._http.get<ProjectApiKey[]>(this.urls.PROJECT_API_KEYS(projectId));
  }

  public createApiKey(projectId: string, name: string): Observable<CreatedProjectApiKey> {
    return this._http.post<CreatedProjectApiKey>(this.urls.PROJECT_API_KEYS(projectId), { name });
  }

  public deleteApiKey(projectId: string, keyId: string): Observable<void> {
    return this._http.delete<void>(this.urls.PROJECT_API_KEY_BY_ID(projectId, keyId));
  }

  public getOrganizationApiKeys(): Observable<ProjectApiKey[]> {
    return this._http.get<ProjectApiKey[]>(this.urls.OWN_ORGANIZATION_API_KEYS);
  }

  public createOrganizationApiKey(name: string): Observable<CreatedProjectApiKey> {
    return this._http.post<CreatedProjectApiKey>(this.urls.OWN_ORGANIZATION_API_KEYS, { name });
  }

  public deleteOrganizationApiKey(keyId: string): Observable<void> {
    return this._http.delete<void>(this.urls.OWN_ORGANIZATION_API_KEY_BY_ID(keyId));
  }

  public uploadProjectLogo(projectId: string, file: File): Observable<{ logo_url: string }> {
    const body = new FormData();
    body.append('file', file);
    return this._http.put<{ logo_url: string }>(this.urls.PROJECT_LOGO(projectId), body);
  }

  public deleteProjectLogo(projectId: string): Observable<void> {
    return this._http.delete<void>(this.urls.PROJECT_LOGO(projectId));
  }
}
